/**
 * AuditNotificationHandler.tsx — Interacciones con notificaciones locales:
 * navega a Auditoría/Dashboard y marca como leído (tap o acción rápida).
 *
 * QUÉ HACE: Se monta una sola vez (en App.tsx, dentro del Router).
 * 1) Registra (idempotente, best-effort) los canales Android por categoría y
 *    la acción rápida "Marcar como leído" — se hace aquí y no solo al activar
 *    una categoría en Ajustes, para que también quede registrada en
 *    dispositivos que ya habían concedido el permiso antes de que existiera
 *    esta función (si no, el botón nunca aparece en iOS).
 * 2) iOS: escucha el toque de una notificación de evento (Ventas/Inventario/
 *    Personal/Sistema) vía Capacitor Local Notifications
 *    (`onNotificationClick`). Tanto el toque normal como el botón "Marcar
 *    como leído" navegan a `/auditoria?openLogId=<id>` — la única diferencia
 *    es que el botón, además, persiste `auditoriaService.updateLogStatus`.
 * 3) Android: estas mismas notificaciones de evento las dispara el plugin
 *    nativo propio `AltenotShellPlugin` (BigPictureStyle — ver platform.ts,
 *    `notifications.send`), NO Capacitor Local Notifications. Tanto el toque
 *    como "Marcar como leído" llegan como deep-link
 *    `altenot://auditoria?openLogId=<id>[&markRead=1]` vía `@capacitor/app`
 *    (`appUrlOpen`), parseado con `URL`/`searchParams` más abajo.
 * 4) Escucha también el toque de la notificación de Meta del Día (barra de
 *    progreso nativa, mismo plugin `AltenotShellPlugin`): llega como
 *    deep-link `altenot://dashboard` y navega a `/dashboard`.
 * QUÉ INVOCA: notifications.onNotificationClick, ensureNotificationChannels,
 *           ensureNotificationActionTypes (@/lib/platform, @/lib/push),
 *           auditoriaService.updateLogStatus, App (@capacitor/app), useNavigate.
 * QUÉ EXPORTA: AuditNotificationHandler (named)
 * QUÉ NO DEBE HACER: No debe renderizar nada visible (retorna null). No-op en
 *           Web/Escritorio (no hay evento estándar de "notificación tocada").
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { App } from "@capacitor/app";
import { notifications } from "@/lib/platform";
import {
  MARK_AS_READ_ACTION_ID,
  ensureNotificationChannels,
  ensureNotificationActionTypes,
} from "@/lib/push";
import { auditoriaService } from "../services/auditoria.service";

export function AuditNotificationHandler(): null {
  const navigate = useNavigate();

  useEffect(() => {
    // Best-effort, idempotente: garantiza que el canal y el botón de acción
    // rápida existan aunque el permiso ya se hubiera concedido antes.
    ensureNotificationChannels().catch(() => {
      /* no-op */
    });
    ensureNotificationActionTypes().catch(() => {
      /* no-op */
    });

    const unsubscribeClick = notifications.onNotificationClick(
      (data, actionId) => {
        if (!data.logId) return;

        if (actionId === MARK_AS_READ_ACTION_ID) {
          auditoriaService.updateLogStatus(data.logId, true).catch(() => {
            /* no-op: acción best-effort */
          });
          // Notifica globalmente que un log fue marcado como leído desde la notificación
          window.dispatchEvent(
            new CustomEvent("auditlog:marked-read", {
              detail: { logId: data.logId },
            }),
          );
        }

        if (data.screen === "auditoria") {
          navigate(`/auditoria?openLogId=${encodeURIComponent(data.logId)}`);
        }
      },
    );

    let removeUrlListener: (() => void) | undefined;
    App.addListener("appUrlOpen", ({ url }) => {
      if (url.includes("dashboard")) {
        navigate("/dashboard");
        return;
      }
      // Android: las notificaciones de evento (Ventas/Inventario/Personal/
      // Sistema) ya NO pasan por Capacitor Local Notifications (ver
      // platform.ts → notifications.send, rama isAndroid) — usan el plugin
      // nativo AltenotShellPlugin con deep-links `altenot://auditoria`, tanto
      // para el toque normal como para el botón "Marcar como leído"
      // (`markRead=1`). iOS sigue usando el listener `onNotificationClick` de
      // abajo, sin cambios.
      if (url.includes("auditoria")) {
        try {
          const parsed = new URL(url);
          const logId = parsed.searchParams.get("openLogId");
          const markRead = parsed.searchParams.get("markRead") === "1";
          if (!logId) return;
          if (markRead) {
            auditoriaService.updateLogStatus(logId, true).catch(() => {
              /* no-op: acción best-effort */
            });
            window.dispatchEvent(
              new CustomEvent("auditlog:marked-read", { detail: { logId } }),
            );
          }
          navigate(`/auditoria?openLogId=${encodeURIComponent(logId)}`);
        } catch {
          /* no-op: URL malformada, se ignora */
        }
      }
    })
      .then((handle) => {
        removeUrlListener = () => handle.remove();
      })
      .catch(() => {
        /* no-op: solo aplica en nativo */
      });

    return () => {
      unsubscribeClick();
      removeUrlListener?.();
    };
  }, [navigate]);

  return null;
}
